import {Record, OrderedMap} from 'immutable';

import {match, makeReducer} from 'web-app/util/redux';
import * as InquiryModalsActions from 'web-app/react/modals-container/inquiry-modals/actions';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';

import * as Actions from './actions';
import {type ExtendedInquiry} from './types';

type UpdateInquirySuccess = ReturnType<typeof InquiryModalsActions.updateInquirySuccess.action>;

const updateInquiry = (state: InquiriesState, payload: UpdateInquirySuccess) => {
    return state.merge({
        inquiries: state.inquiries.set(payload.payload.inquiry.inquiryId, payload.payload.inquiry),
    });
};

interface State {
    inquiries: OrderedMap<string, ExtendedInquiry>;
    behaviors: BehaviorsCollection;
    loadingInquiries: boolean;
    csv: string | null;
    total: number;
    nextOffset: number | null;
    loadingMore: boolean;
    lost: number;
    enrolled: number;
    showarounds: number;
    inquiriesLoaded: boolean;
}

export class InquiriesState extends Record<State>({
    inquiries: OrderedMap(),

    behaviors: new BehaviorsCollection([]),
    loadingInquiries: false,
    csv: null,
    total: 0,
    nextOffset: null,
    loadingMore: false,
    lost: 0,
    enrolled: 0,
    showarounds: 0,
    inquiriesLoaded: false,
}) {}

export default makeReducer<InquiriesState>(
    [
        match(Actions.fetchInquiries, state => state.merge({loadingInquiries: true, nextOffset: null})),

        match(Actions.fetchInquiriesSuccess, (state, {payload}) => {
            return state.merge({
                inquiries: payload.inquiries,
                behaviors: payload.behaviors,
                csv: payload.csv,
                loadingInquiries: false,
                total: payload.total,
                nextOffset: payload.nextOffset,
                lost: payload.lost,
                enrolled: payload.enrolled,
                showarounds: payload.showarounds,
                inquiriesLoaded: true,
            });
        }),
        match(Actions.fetchInquiriesFailed, state =>
            state.merge({
                loadingInquiries: false,
                inquiriesLoaded: true,
            }),
        ),

        match(Actions.fetchMoreInquiries, state => state.merge({loadingInquiries: true})),
        match(Actions.fetchMoreInquiriesSuccess, (state, {payload}) => {
            return state.merge({
                inquiries: state.inquiries.merge(payload.inquiries),
                loadingInquiries: false,
                nextOffset: payload.nextOffset,
            });
        }),
        match(Actions.fetchMoreInquiriesFailed, state =>
            state.merge({
                loadingInquiries: false,
            }),
        ),

        match(InquiryModalsActions.updateInquirySuccess, updateInquiry),
        match(InquiryModalsActions.startInSuccess, updateInquiry),
        match(InquiryModalsActions.deleteInquirySuccess, (state, {payload}) =>
            state.merge({inquiries: state.inquiries.delete(payload.inquiryId)}),
        ),
        match(InquiryModalsActions.updateInquiryActionSuccess, (state, {payload}) => {
            let updatedInquiry = state.inquiries.get(payload.inquiryAction.inquiryId);
            if (updatedInquiry) {
                updatedInquiry = updatedInquiry.merge({
                    actions: updatedInquiry.actions.map(action => {
                        return action.inquiryActionId === payload.inquiryAction.inquiryActionId
                            ? payload.inquiryAction
                            : action;
                    }),
                });

                return state.merge({
                    inquiries: state.inquiries.set(payload.inquiryAction.inquiryId, updatedInquiry),
                });
            }
            return state;
        }),
        match(InquiryModalsActions.deleteInquiryActionSuccess, (state, {payload}) => {
            let updatedInquiry = state.inquiries.get(payload.inquiryAction.inquiryId);
            if (updatedInquiry) {
                updatedInquiry = updatedInquiry.merge({
                    actions: updatedInquiry.actions.filter(
                        action => action.inquiryActionId !== payload.inquiryAction.inquiryActionId,
                    ),
                });

                return state.merge({
                    inquiries: state.inquiries.set(payload.inquiryAction.inquiryId, updatedInquiry),
                });
            }
            return state;
        }),
        match(InquiryModalsActions.createInquiryActionSuccess, (state, {payload}) => {
            let updatedInquiry = state.inquiries.get(payload.inquiryAction.inquiryId);
            if (updatedInquiry) {
                updatedInquiry = updatedInquiry.merge({
                    actions: updatedInquiry.actions.insert(0, payload.inquiryAction),
                });

                return state.merge({
                    inquiries: state.inquiries.set(payload.inquiryAction.inquiryId, updatedInquiry),
                });
            }
            return state;
        }),
        match(Actions.clearInquiries, () => new InquiriesState()),
    ],
    new InquiriesState(),
);
